import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import icons from '../config/icons.config'
import { useModal } from '../contexts/ModalContext'
import { useRecoverySession } from '../hooks/auth/useRecoverySession'
import { useUpdatePassword } from '../hooks/auth/useUpdatePassword'
import { ScreenPath } from '../navigation'
import { logError } from '../util/errorLogger'
import AppIcon from '../ui/icons/AppIcon'
import styles from '../ui/auth/AuthForm.module.css'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Button from '../ui/primitives/Button'
import TextField from '../ui/primitives/TextField'

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

export default function ResetPasswordScreen() {
  const navigate = useNavigate()
  const { showSnackbar } = useModal()
  const recoveryStatus = useRecoverySession()
  const { mutateAsync: updatePassword } = useUpdatePassword()
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  async function onSubmit(data: ResetPasswordForm) {
    try {
      setIsLoading(true)
      await updatePassword(data.password)
      showSnackbar(
        'reset-success',
        'Password successfully reset! Please log in with your new password.',
        'success',
      )
      navigate(ScreenPath.login, { replace: true })
    } catch (e) {
      void logError(e, { screen: 'ResetPassword' }, undefined, 'onSubmit')
      const rawMessage = e instanceof Error ? e.message : 'An unknown error occurred'
      const isSessionMissing = /session missing|auth session missing/i.test(rawMessage)
      const userMessage = isSessionMissing
        ? "Reset link couldn't be verified. Request a new link and open it in this browser."
        : 'Something went wrong. Please try again or request a new reset link.'
      showSnackbar('reset-error', userMessage, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  if (recoveryStatus === 'loading') {
    return (
      <SimplePageLayout title="Reset password">
        <div className={styles.card}>
          <p className={styles.hint}>Setting up reset…</p>
        </div>
      </SimplePageLayout>
    )
  }

  if (recoveryStatus === 'expired') {
    return (
      <SimplePageLayout title="Reset password">
        <div className={styles.card}>
          <p className={styles.hint}>
            This reset link didn&apos;t work. It may be invalid, expired, or opened in a different
            browser. Request a new link and open it in the same browser where you&apos;ll set your
            password.
          </p>
          <div className={styles.actions}>
            <Button onClick={() => navigate(ScreenPath.forgotPassword)}>Send a new reset link</Button>
          </div>
        </div>
      </SimplePageLayout>
    )
  }

  return (
    <SimplePageLayout title="Reset password">
      <div className={styles.card}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <p className={styles.hint}>Enter your new password below.</p>
          <TextField
            label="New password"
            type={showPassword ? 'text' : 'password'}
            error={errors.password?.message}
            endAdornment={
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
              >
                <AppIcon icon={showPassword ? icons.eyeOff : icons.eye} size={18} />
              </button>
            }
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters long' },
            })}
          />
          <TextField
            label="Confirm password"
            type={showPassword ? 'text' : 'password'}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === watch('password') || 'Passwords do not match',
            })}
          />
          <div className={styles.actions}>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving…' : 'Reset password'}
            </Button>
          </div>
        </form>
      </div>
    </SimplePageLayout>
  )
}
